import { createApi } from "@reduxjs/toolkit/query/react";
import { baseAppQuery } from "shared/api/baseQuery";
import { Filter } from "shared/types/filters";
import { SignatureData, SignatureInfo } from "features/signature/types/signature";
import { NoticeInfo } from "features/notice/types/notice";
import { AdditionalContractInfo } from "features/additionalContract/types/additionalContract";
import { snackbarOnQueryStarted } from "middleware/snackbarOnQueryStarted";

const ENDPOINT = "/signature";
const signatureApi: any = createApi({
  reducerPath: "signatureApi",
  baseQuery: baseAppQuery,
  keepUnusedDataFor: 0,
  tagTypes: ["signature", "appDocumentType", "appDocumentTypeSource"],
  refetchOnMountOrArgChange: true,
  endpoints: (builder) => ({
    getSignatureListCurrent: builder.query<SignatureData, Filter>({
      query: (filter) => {
        const queryParams = new URLSearchParams();
        queryParams.set("page", filter.pageNumber.toString());
        queryParams.set("size", filter.pageSize.toString());
        queryParams.set("sort", "createTimestamp,desc")
        return `${ENDPOINT}/fetch-all-for-current?${queryParams}`;
      },
      providesTags: ["signature"],
    }),
    getSignatureCurrentById: builder.query<SignatureData, string>({
      query: (signatureId)=> `${ENDPOINT}/fetch-one-for-current/${signatureId}` 
    }),
    getSignedSignatureByApplicationId: builder.query<
      SignatureData,
      { id: string; filter: Filter }
    >({
      query: ({ id, filter }) => {
        const queryParams = new URLSearchParams();
        if (filter) {
          queryParams.set("pageNumber", filter.pageNumber.toString());
          queryParams.set("pageSize", filter.pageSize.toString());
        }
        return `${ENDPOINT}/fetch-all-signed-for-current/${id}?${queryParams}`;
      },
    }),
    getSignatureTemplate: builder.query<SignatureData, string>({
      query: (id) => ({
        url: `${ENDPOINT}/signature-template/${id}`,
        responseHandler: (response) => response.blob().then((blob) => blob),
      }),
    }),
    getSignatureTemplateBase64: builder.query<string, string>({
      query: (id) => ({
        url: `${ENDPOINT}/signature-template/base64/${id}`,
        responseHandler: (response) => response.text(),
      }),
    }),
    getSignatureChainCmsBase64: builder.query<string, string>({
      query: (id) => ({
        url: `${ENDPOINT}/chain/cms/base64/${id}`,
        responseHandler: (response) => response.text(),
      }),
    }),
    getSignatureChainPdf: builder.query<string, string>({
      query: (id) => ({
        url: `${ENDPOINT}/chain/pdf/${id}`,
        responseHandler: (response) => response.blob().then((blob) => blob),
      }),
    }),
    getByAppDocumentType: builder.query<
      SignatureInfo,
      { applicationId: string; documentTypeId: string }
    >({
      query: ({ applicationId, documentTypeId }) =>
        `${ENDPOINT}/get-by-app-document-type?applicationId=${applicationId}&documentTypeId=${documentTypeId}`,
      providesTags: ["appDocumentType"],
    }),
    getByAppDocumentTypeSource: builder.query<SignatureInfo, {applicationId: string, documentTypeId:string, sourceId:string,}>({
      query:({applicationId, documentTypeId, sourceId})=> 
        `${ENDPOINT}/get-by-app-document-type-source?applicationId=${applicationId}&documentTypeId=${documentTypeId}&sourceId=${sourceId}`,
      providesTags: ["appDocumentTypeSource"],
    }),
    getSignaturePreviewTemplate: builder.query<
      string,
      { sourceId: string; documentTypeId: string }
    >({
      query: ({ sourceId, documentTypeId }) => ({
        url: `${ENDPOINT}/preview-template/${sourceId}/${documentTypeId}`,
        responseHandler: (response) => response.blob().then((blob) => blob),
      }),
    }),
    createAssignApprovals: builder.mutation<SignatureData, string>({
      query: (id) => ({
        url: `${ENDPOINT}/assign-approvals?applicationId=${id}`,
        method: "POST",
      }),
       onQueryStarted: snackbarOnQueryStarted({
        successMessage:"successApplicationForApproval",
        errorMessage:"errorApplicationForApproval",
      })
    }),
    createApprovalDecision: builder.mutation<SignatureData, string>({
      query: (id) => ({
        url: `${ENDPOINT}/create-approval-decision?applicationId=${id}`,
        method: "POST",
      }),
      invalidatesTags: ["appDocumentType"],
      onQueryStarted: snackbarOnQueryStarted({
        errorMessage:"submitToApprovalError",
      })
    }),
    createNoticeSignature: builder.mutation<
      NoticeInfo,
      { id: string}
    >({
      query: ({ id }) => ({
        url: `${ENDPOINT}/create-notice?noticeId=${id}`,
        method: "POST",
      }),
      onQueryStarted: snackbarOnQueryStarted({
        successMessage:"noticeCreateSuccess",
        errorMessage:"noticeCreateError",
      })
    }),
    createAdditionalContractSignature: builder.mutation<AdditionalContractInfo, string>({
      query:(id)=>({
        url:`${ENDPOINT}/create-additional-contract?additionalContractId=${id}`,
        method:"POST"
      }),
      invalidatesTags: ["appDocumentTypeSource"],
      onQueryStarted: snackbarOnQueryStarted({
        errorMessage:"signAdditionalContractError",
      })
    }),
    verifyCmsSignature: builder.mutation<SignatureData, string>({
      query: (data) => {
        let formData = new FormData();
        formData.append("data", data);
        return {
          url: `${ENDPOINT}/cms/verify`,
          method: "POST",
          body: formData,
        };
      },
      onQueryStarted: snackbarOnQueryStarted({
        successMessage:"confirmationSucceeded",
        errorMessage:"confirmationFailed",
      })
    }),
    verifyAttachCmsSignature: builder.mutation<
    SignatureData,
    { signatureId: string; data: string; comment: string }
  >({
    query: ({ signatureId, data, comment }) => {
      let formData = new FormData();
      formData.append("signatureId", signatureId);
      formData.append("data", data);
      formData.append("comment", comment);

      return {
        url: `${ENDPOINT}/cms/verify-attach`,
        method: "POST",
        body: formData,
      };
    },
    invalidatesTags: ["signature"],
    onQueryStarted: snackbarOnQueryStarted({
      successMessage:"confirmationSucceeded",
      errorMessage:"confirmationFailed",
    })
  }),
    verifyAttachChainCmsSignature: builder.mutation<
    SignatureData,
    { signatureId: string; data: string; comment: string }
  >({
    query: ({ signatureId, data, comment }) => {
      let formData = new FormData();
      formData.append("signatureId", signatureId);
      formData.append("data", data);
      formData.append("comment", comment);
      return {
        url: `${ENDPOINT}/chain/cms/verify-attach`,
        method: "POST",
        body: formData,
      };
    },
    invalidatesTags: ["signature"],
     onQueryStarted: snackbarOnQueryStarted({
      successMessage:"confirmationSucceeded",
      errorMessage:"confirmationFailed",
    })
  }),
   
  }),
});

export const {
  useGetSignatureListCurrentQuery,
  useGetSignatureCurrentByIdQuery,
  useGetSignedSignatureByApplicationIdQuery,
  useGetSignatureTemplateQuery,
  useLazyGetSignatureTemplateQuery,
  useGetSignatureChainCmsBase64Query,
  useLazyGetSignatureChainCmsBase64Query,
  useGetSignatureChainPdfQuery,
  useGetByAppDocumentTypeQuery,
  useGetByAppDocumentTypeSourceQuery,
  useLazyGetByAppDocumentTypeQuery,
  useGetSignatureTemplateBase64Query,
  useLazyGetSignatureTemplateBase64Query,
  useGetSignaturePreviewTemplateQuery,
  useLazyGetSignaturePreviewTemplateQuery,
  useCreateAssignApprovalsMutation,
  useCreateApprovalDecisionMutation,
  useCreateNoticeSignatureMutation,
  useCreateAdditionalContractSignatureMutation,
  useVerifyCmsSignatureMutation,
  useVerifyAttachCmsSignatureMutation,
  useVerifyAttachChainCmsSignatureMutation,
} = signatureApi;
export default signatureApi;
